/**
 * patrol-tracking-edit.js
 */

import React from 'react';
import {
  Grid,
  Button,
  IconButton,
  Box,
  Tooltip,
  Alert,
  Typography,
} from '@mui/material';
import { useFormContext, useWatch, useFieldArray } from 'react-hook-form';
import { parseISO, formatISO9075, isValid, differenceInHours } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { shouldRenderMobileLayout } from 'utils/app-context';
import { Types } from 'utils/constants';
import { displayNumber } from 'utils/misc';
import { convertDistance } from 'utils/distance';
import { UserContext } from 'contexts/user/user-context';
import { EditorIconSection } from 'components/icon-section/icon-section';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import FormSectionTitle from 'components/menu-drawer/form-section-title';
import EditorAccordion from 'components/menu-drawer/editor-accordion';
import HiddenInput from 'components/form-inputs/hidden-input';
import Checkbox from 'components/form-inputs/checkbox';
import DateTimePicker from 'components/form-inputs/date-time-picker';
import TextField from 'components/form-inputs/text-field';
import CheckboxList from 'components/form-inputs/checkbox-list';
import Select from 'components/form-inputs/select';
import Icon from 'components/icon';
import { ExerciseFollowMap, fetchTracks } from 'components/tracking/map/viewing-map';
import { TRACK_TYPE } from 'components/tracking/tracking-common';
import { PatrolTypeTitle } from '../../training-utils';


export default function PatrolTrackingEdit(props) {
  const {
    answerId,
    storedRecord,
    patrolRecord,
    patrolIndex,
    patrolTypes,
    drawerState,
    translation,
    options,
    formPrefix = '',
  } = props;
  const formNameBase = `${formPrefix}patrol.patrolTypes[${patrolIndex}].tracking`;
  const typeRecord = patrolRecord?.tracking;
  const { t } = useTranslation(translation);
  const { control, setValue } = useFormContext();
  const distanceUnitValue = useWatch({ control, name: `${formNameBase}.trackDistanceUnit` });
  const distance = useWatch({ control, name: `${formNameBase}.trackDistance` });
  // the distance and unit first entered, used for more accurate conversions between units.
  const [distanceInput, setDistanceInput] = React.useState({ 
    value: distance, 
    unit: distanceUnitValue?.id 
  });
  const [isFirstTrackingType] = React.useState(
    patrolTypes.findIndex(pt => pt.type === Types.Patrol.TRACKING) === patrolIndex
  );
  const { user } = React.useContext(UserContext);
  const [tracks, setTracks] = React.useState();

  const itemsArrayKeyName = 'itemsFormId';
  const { fields: trackingItems, append, remove } = useFieldArray({
    control,
    name: `${formNameBase}.items`,
    keyName: itemsArrayKeyName
  });


  // set default distance unit
  React.useEffect(() => {
    if (distanceUnitValue === null || distanceUnitValue === undefined) {
      setValue(`${formNameBase}.trackDistanceUnit`, options.distanceUnits[0]);
    }
  }, [distanceUnitValue, formNameBase, options, setValue]);


  // set blank tracking item if list is empty
  React.useEffect(() => {
    if (trackingItems.length === 0) {
      append({ id: null, name: null });
    }
  }, [append, trackingItems.length]);


  // query tracks so we can determine if the user currently has an incomplete track.
  React.useEffect(() => {
    if (patrolRecord?.tracking?.hasTrack && patrolRecord?.tracking?.id && answerId && user) {
      (async () => {
        const localTracks = await fetchTracks(TRACK_TYPE.FOLLOW, answerId, patrolRecord.tracking.id, user);
        setTracks(localTracks);
      })();
    }
  }, [answerId, patrolRecord?.tracking?.hasTrack, patrolRecord?.tracking?.id, user]);


  // track might still be active if it doesn't have the complete flag set 
  // (i.e. if a save/complete button wasn't pressed) and if it's been less
  // than 3 hours since the last point was created.
  const followTrack = tracks && tracks?.find(t => t.type === TRACK_TYPE.FOLLOW);
  const isTrackActive = followTrack
    && !followTrack?.isComplete
    && followTrack.points.length > 0    
    && differenceInHours(new Date(), followTrack.points[followTrack.points.length - 1].dateTimeCreated) < 3;
  
    
  // convert the tracking distance to the selected unit
  const distanceUnitOnChange = (event) => {
    const oldUnit = options.distanceUnits.find(unit => unit.id === distanceInput.unit)?.name;
    const newUnit = options.distanceUnits.find(unit => unit.id === event.target.value)?.name;
    
    const converted = convertDistance(distanceInput.value, oldUnit, newUnit);
    
    if (converted !== null) {
      setValue(`${formNameBase}.trackDistance`, displayNumber(converted));
    }
  }


  return <>
    <EditorIconSection
      iconName='patrol-tracking'
      iconMarginTop={0}
      helpProps={{
        translation,
        section: 'tracking',
        suffix: patrolTypes.length > 1 ? ` (Scenario)` : '',
      }}
    >
      {isTrackActive &&
        <Alert severity='error' sx={{ marginBottom: 2 }}>
          <Typography>
            IMPORTANT NOTE: This track wasn't saved when it was stopped and may not be done.
            To prevent data loss, please save this track in the PACKTRACK mobile app first before completing it here.
          </Typography>
        </Alert>
      }

      {!shouldRenderMobileLayout() &&
      <PatrolTypeTitle 
        patrolType={patrolRecord.type} 
        patrolTypes={patrolTypes} 
        step={patrolRecord.order}
      />}

      {/* id */}
      <HiddenInput 
        name={`${formNameBase}.id`}
        defaultValue={typeRecord?.id || ''}
      />


      {/* known location */}
      <HelpTextItem
        sidebarText={drawerState.sidebarText}
        title={t('tracking.knownLocation.helpTitle')}
        text={t('tracking.knownLocation.helpInfo')}
      >
        <Tooltip
          arrow
          placement='top'
          title={typeRecord?.canChangeKnownLocation === false 
            ? `This cannot be changed because the laid track was revealed when tracking`
            : ''}
        >
          <span style={{ width: 'fit-content' }}>
            <Checkbox
              name={`${formNameBase}.isKnownLocation`}
              label={t('tracking.knownLocation.label')}
              defaultValue={typeRecord?.isKnownLocation || false}
              disabled={typeRecord?.canChangeKnownLocation === false}
            />
          </span>
        </Tooltip>
      </HelpTextItem>


      {/* controlled negative */}
      <HelpTextItem
        sidebarText={drawerState.sidebarText}
        title={t('tracking.controlledNegative.helpTitle')}
        text={t('tracking.controlledNegative.helpInfo')}
      >
        <Checkbox
          name={`${formNameBase}.isControlledNegative`}
          label={t('tracking.controlledNegative.label')}
          defaultValue={typeRecord?.isControlledNegative}
        />
      </HelpTextItem>

      {/* evidence items */}
      <HelpTextItem
        sidebarText={drawerState.sidebarText}
        title={t('tracking.evidence.helpTitle')}
        text={t('tracking.evidence.helpInfo')}
        style={{ marginTop: 8 }}
      >
        {focusHandlers =>
          <>
            <FormSectionTitle style={{ marginBottom: 8 }}>
              {t('tracking.evidence.label')}
            </FormSectionTitle>
            <Grid 
              container 
              spacing={1} 
              key={storedRecord.id}
            >
              {trackingItems.map((item, index) => (
                <React.Fragment key={`${storedRecord.id}-${item[itemsArrayKeyName]}`}>
                <HiddenInput
                  name={`${formNameBase}.items[${index}].id`}
                  defaultValue={item.id}
                />
                <FormGridItem xs={8} sm={8} md={8} lg={6} xl={5}>
                  <TextField
                    name={`${formNameBase}.items[${index}].name`}
                    label={`Item ${index + 1}`}
                    placeholder={`Item ${index + 1} Name`}
                    focusHandlers={focusHandlers}
                    defaultValue={typeRecord?.items?.find(i => i.id === item.id)?.name || ''}
                    rules={{
                      maxLength: {
                        value: 250,
                        message: t('tracking.evidence.errMsgLength')
                      }
                    }}
                  />
                </FormGridItem>
                <FormGridItem xs={4} sm={4} md={4} lg={6} xl={7}>
                  <Box display="flex" flexDirection="row" height="100%">
                    { trackingItems.length > 1 &&
                    <IconButton onClick={() => remove(index)} title="remove item" size="large">
                      <Icon type="delete" />
                    </IconButton>}
                    { (index === trackingItems.length - 1) &&
                    <Button
                      color="primary"
                      size="small"
                      onClick={() => append({ id: 0, name: ''})} >
                        &#43;&nbsp;ITEM
                    </Button>}
                  </Box>
                </FormGridItem>
             </React.Fragment>
              ))}
            </Grid>
          </>}
      </HelpTextItem>


      {/* terrain types */}
      <HelpTextItem
        sidebarText={drawerState.sidebarText}
        title={t('tracking.terrainTypes.helpTitle')}
        text={t('tracking.terrainTypes.helpInfo')}
        style={{ marginTop: 16 }}
      >
        {focusHandlers =>
          <CheckboxList
            name={`${formNameBase}.terrainTypes`}
            label={t('tracking.terrainTypes.label')}
            options={options.terrainTypes}
            focusHandlers={focusHandlers}
            defaultValue={typeRecord?.terrainTypes}
            customOther
            rules={{
              validate: val => (val && val.length > 0) || t('tracking.terrainTypes.errMsgEmpty')
            }}
          />}
      </HelpTextItem>


      {/* tracking data */}
      <HelpTextItem
        sidebarText={drawerState.sidebarText}
        title={t('tracking.trackingData.helpTitle')}
        text={t('tracking.trackingData.helpInfo')}
        style={{ marginTop: 16, minHeight: 32 }}
      >
        <FormSectionTitle>
          {t('tracking.trackingData.label')}
        </FormSectionTitle>
      </HelpTextItem>


      {/* track name */}
      <HelpTextItem
        sidebarText={drawerState.sidebarText}
        title={t('tracking.name.helpTitle')}
        text={t('tracking.name.helpInfo')}
      >
        {focusHandlers =>
          <Grid container spacing={2}>
            <FormGridItem>
              <TextField
                name={`${formNameBase}.trackLayerName`}
                label={t('tracking.name.label')}
                focusHandlers={focusHandlers}
                defaultValue={typeRecord?.trackLayerName || ''}
                rules={{
                  required: t('tracking.name.errMsgEmpty'),
                  validate: value => value.trim() !== '' || t('tracking.name.errMsgEmpty'),
                  maxLength: {
                    value: 250,
                    message: t('tracking.name.errMsgLength')
                  }
                }}
              />
            </FormGridItem>
          </Grid>}
      </HelpTextItem>


      {/* track duration */}
      <HelpTextItem
        sidebarText={drawerState.sidebarText}
        title={t('tracking.duration.helpTitle')}
        text={t('tracking.duration.helpInfo')}
      >
        {focusHandlers =>
          <Grid container spacing={2}>
            <FormGridItem>
              <TextField
                name={`${formNameBase}.trackDuration`}
                label={t('tracking.duration.label')}
                type='number'
                focusHandlers={focusHandlers}
                defaultValue={typeRecord?.trackDuration || ''}
                rules={{
                  required: t('tracking.duration.errMsgEmpty'),
                  max: {
                    value: 999999999,
                    message: t('tracking.duration.errMsgSize')
                  },
                  validate: val =>
                    (Number(val) === parseInt(val) && parseInt(val) > 0)
                    || t('tracking.duration.errMsgInteger')
                }}
              />
            </FormGridItem>
          </Grid>}
      </HelpTextItem>
      

      <EditorAccordion
        highlight
        name="optional fields"
        summary={<FormSectionTitle>OPTIONAL: Tracking, Contaminants</FormSectionTitle>}
        detail={
          <>
            {/* track distance */}
            <HelpTextItem
              sidebarText={drawerState.sidebarText}
              title={t('tracking.distance.helpTitle')}
              text={t('tracking.distance.helpInfo')}
            >
              {focusHandlers =>
                <Grid container spacing={2}>

                  {/* distance */}
                  <FormGridItem xs={6} sm={6}>
                    <TextField
                      name={`${formNameBase}.trackDistance`}
                      label={t('tracking.distance.label')}
                      type='number'
                      focusHandlers={focusHandlers}
                      defaultValue={typeRecord?.trackDistance || ''}
                      onChange={e => setDistanceInput({ 
                        value: e.target.value, 
                        unit: distanceUnitValue.id 
                      })}
                    />
                  </FormGridItem>

                  {/* distance unit */}
                  <FormGridItem xs={6} sm={6}>
                    <Select
                      name={`${formNameBase}.trackDistanceUnit`}
                      options={options.distanceUnits}
                      focusHandlers={focusHandlers}
                      defaultValue={typeRecord?.trackDistanceUnit}
                      style={{ marginTop: 4 }}
                      setValueAs={id => ({ id })}
                      onChange={distanceUnitOnChange}
                    />
                  </FormGridItem>

                </Grid>}
            </HelpTextItem>

            {/* track times */}
            <HelpTextItem
              sidebarText={drawerState.sidebarText}
              title={t('tracking.times.helpTitle')}
              text={t('tracking.times.helpInfo')}
            >
              {focusHandlers =>
                <Grid container spacing={2}>

                  {/* track laid time */}
                  <FormGridItem>
                    <DateTimePicker
                      variant='time'
                      name={`${formNameBase}.trackLaidTime`}
                      label={t('tracking.times.labelLaid')}
                      focusHandlers={focusHandlers}
                      defaultValue={typeRecord?.trackLaidTime}
                      clearable
                      initialFocusedDate={parseISO(storedRecord.eventDate)}
                      setValueAs={date => {
                        if (date && isValid(date)) {
                          // combine record date with time input
                          const eventDate = parseISO(storedRecord.eventDate);
                          const combined = new Date(
                            eventDate.getFullYear(), 
                            eventDate.getMonth(), 
                            eventDate.getDate(), 
                            date.getHours(), 
                            date.getMinutes());
                          return formatISO9075(combined); // no-timezone string
                        }
                        return date;
                      }} 
                      format='HH:mm'
                    />
                  </FormGridItem>

                  {/* track followed time */}
                  <FormGridItem>
                    <DateTimePicker
                      variant='time'
                      name={`${formNameBase}.trackFollowedTime`}
                      label={t('tracking.times.labelFollowed')}
                      focusHandlers={focusHandlers}
                      defaultValue={typeRecord?.trackFollowedTime}
                      clearable
                      initialFocusedDate={parseISO(storedRecord.eventDate)}
                      setValueAs={date => {
                        if (date && isValid(date)) {
                          // combine record date with time input
                          const eventDate = parseISO(storedRecord.eventDate);
                          const combined = new Date(
                            eventDate.getFullYear(), 
                            eventDate.getMonth(), 
                            eventDate.getDate(), 
                            date.getHours(), 
                            date.getMinutes());
                          return formatISO9075(combined); // no-timezone string
                        }
                        return date;
                      }} 
                      format='HH:mm'
                    />
                  </FormGridItem>

                </Grid>}
            </HelpTextItem>

            {/* track turns and crossings */}
            <HelpTextItem
              sidebarText={drawerState.sidebarText}
              title={t('tracking.turnsCrossings.helpTitle')}
              text={t('tracking.turnsCrossings.helpInfo')}
            >
              {focusHandlers =>
                <Grid container spacing={2}>

                  {/* track turns */}
                  <FormGridItem>
                    <TextField
                      name={`${formNameBase}.trackTotalTurns`}
                      label={t('tracking.turnsCrossings.labelTurns')}
                      focusHandlers={focusHandlers}
                      defaultValue={typeRecord?.trackTotalTurns || ''}
                      type='number'
                      rules={{
                        max: {
                          value: 999999999,
                          message: t('tracking.turnsCrossings.errMsgSize')
                        },
                      }}
                    />
                  </FormGridItem>

                  {/* track crossings */}
                  <FormGridItem>
                    <TextField
                      name={`${formNameBase}.trackTotalHumansCrossing`}
                      label={t('tracking.turnsCrossings.labelCrossings')}
                      focusHandlers={focusHandlers}
                      defaultValue={typeRecord?.trackTotalHumansCrossing || ''}
                      type='number'
                      rules={{
                        max: {
                          value: 999999999,
                          message: t('tracking.turnsCrossings.errMsgSize')
                        },
                      }}
                    />
                  </FormGridItem>

                </Grid>}
            </HelpTextItem>

            {/* contaminant types */}
            <HelpTextItem
              sidebarText={drawerState.sidebarText}
              title={t('tracking.distractors.helpTitle')}
              text={t('tracking.distractors.helpInfo')}
              style={{ marginTop: 16 }}
            >
              {focusHandlers =>
                <CheckboxList
                  name={`${formNameBase}.contaminantTypes`}
                  label={t('tracking.distractors.label')}
                  options={options.contaminantTypes}
                  focusHandlers={focusHandlers}
                  defaultValue={typeRecord?.contaminantTypes}
                  customOther
                />}
            </HelpTextItem>

          </>
        }
      />      

    </EditorIconSection>


    {isFirstTrackingType && tracks &&
    <ExerciseFollowMap 
      recordId={answerId}
      trackingRecord={patrolRecord.tracking}
      tracks={tracks}
    />}
  </>;
}